import { useEffect, useRef, useState } from "react";
import { PrimaryButton, SecondaryButton } from "../../common/common.style";
import toast from "react-hot-toast";
import { Box, FlexBox } from "../../primitives";
import {
  CardSelected,
  MinimalCardToSendToUploadCsvWorker,
} from "../../../type";
import { useCards } from "../../../context/CardsContext";
import { LoadingOverlay } from "../../common/LoadingOverley/LoadingOverley";
import { Modal } from "../../common/Modal/Modal";
import { IconNameText, WrapperIcon } from "./header.styled";
import { FiDownload, FiUpload } from "react-icons/fi";
import { selectGame } from "../../../store/slices/global";
import { useSelector } from "react-redux";

interface ImportExportCsvProps {
  cardsBoard: CardSelected[];
  setCardsBoard: (arg1: CardSelected[]) => void;
  cardsClicked: string[];
}

const MAX_WORKERS = 5;
const LINES_PER_CHUNK = 100;

export const ImportExportCsv = ({
  cardsBoard,
  setCardsBoard,
  cardsClicked,
}: ImportExportCsvProps) => {
  const [modalExport, setModalExport] = useState(false);
  const cards = useCards();
  const game = useSelector(selectGame);
  const [isLoading, setIsLoading] = useState(false);
  const workersRef = useRef<Worker[]>([]);
  const [importProgress, setImportProgress] = useState({
    total: 0,
    imported: 0,
  });

  // Mutable ref to accumulate errors CSV string synchronously
  const errorsCsvRef = useRef<string>("");

  // Cleanup workers on unmount
  useEffect(() => {
    return () => {
      workersRef.current.forEach((w) => w.terminate());
      workersRef.current = [];
    };
  }, []);

  // Helper: split CSV text into chunks including header line
  const splitCsvIntoChunks = (csvText: string): string[] => {
    const lines = csvText.split("\n");
    const header = lines[0];
    const dataLines = lines.slice(1);

    const chunks: string[] = [];
    for (let i = 0; i < dataLines.length; i += LINES_PER_CHUNK) {
      chunks.push(
        [header, ...dataLines.slice(i, i + LINES_PER_CHUNK)].join("\n")
      );
    }
    return chunks;
  };

  const handleImportCSV = async (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    const file = event.target.files?.[0];
    if (!file) return;

    setIsLoading(true);
    setImportProgress({ total: 0, imported: 0 });

    const fileContent = await file.text();

    // Build minimal card index data keyed by string id
    const minimalCardIndexData: MinimalCardToSendToUploadCsvWorker =
      Object.fromEntries(
        Object.entries(cards)
          .map(([id, card]) => [
            String(card.card_market_ids?.[0]), // key by first card_market_id (string)
            { id, card_market_id: card.card_market_ids?.[0], name: card.name },
          ])
          .filter(([key]) => !!key) // filter out cards without card_market_id
      );

    // Build minimal card index data keyed by string id
    const minimalCardIndexDataCardTrader: MinimalCardToSendToUploadCsvWorker =
      Object.fromEntries(
        Object.entries(cards)
          .map(([id, card]) => [
            String(card.id), // key by first card_market_id (string)
            { id, card_market_id: card.card_market_ids?.[0], name: card.name },
          ])
          .filter(([key]) => !!key) // filter out cards without card_market_id
      );

    const chunks = splitCsvIntoChunks(fileContent);

    // Total cards count for progress
    const totalCardsCount = fileContent.split("\n").length - 1;
    setImportProgress({ total: totalCardsCount, imported: 0 });

    let currentChunkIndex = 0;
    let totalImportedCards: CardSelected[] = [];
    let totalInvalidCount = 0;

    // Reset errors CSV accumulator synchronously before starting
    errorsCsvRef.current = "";

    // Create workers pool
    workersRef.current = Array(MAX_WORKERS)
      .fill(null)
      .map(
        () =>
          new Worker(
            new URL("../../../workers/csvWorker.worker.ts", import.meta.url),
            { type: "module" }
          )
      );

    const workers = workersRef.current;

    // Helper to send next chunk to a worker
    const sendNextChunk = (workerIndex: number) => {
      if (currentChunkIndex >= chunks.length) {
        // No more chunks, terminate worker
        workers[workerIndex].terminate();
        workers[workerIndex].onmessage = null;

        // If all workers terminated, finalize import
        if (workers.every((w) => w.onmessage === null)) {
          setIsLoading(false);
          // @ts-ignore
          setCardsBoard((prev) => [...prev, ...totalImportedCards]);
          toast.success(`Imported ${totalImportedCards.length} cards`);
          if (totalInvalidCount > 0) {
            toast.error(`${totalInvalidCount} invalid row(s) skipped`);

            if (errorsCsvRef.current) {
              // Trigger automatic download of error CSV
              const blob = new Blob([errorsCsvRef.current], {
                type: "text/csv;charset=utf-8;",
              });
              const url = URL.createObjectURL(blob);

              const link = document.createElement("a");
              link.href = url;
              link.download = "import_errors.csv";
              link.click();

              URL.revokeObjectURL(url);
            }
          }
        }
        return;
      }

      const chunk = chunks[currentChunkIndex];
      currentChunkIndex++;

      workers[workerIndex].postMessage({
        type: "IMPORT_CSV_CHUNK",
        payload: {
          file: chunk,
          cardIndexData: minimalCardIndexData,
          minimalCardIndexDataCardTrader,
          game_id: game.id,
        },
      });
    };

    // Setup message handlers and start initial chunks
    workers.forEach((worker, index) => {
      worker.onmessage = (event) => {
        const {
          type,
          importedCards,
          invalidCount,
          error,
          errorsCsv: chunkErrorsCsv,
        } = event.data;

        if (type === "IMPORT_CSV_COMPLETE_CHUNK") {
          totalImportedCards = [...totalImportedCards, ...importedCards];
          totalInvalidCount += invalidCount;

          setImportProgress((prev) => ({
            total: prev.total,
            imported: prev.imported + importedCards.length,
          }));

          // Append any error CSV returned from worker synchronously
          if (chunkErrorsCsv) {
            const chunkLines = chunkErrorsCsv.split("\n");

            if (errorsCsvRef.current === "") {
              // First chunk, keep header line
              errorsCsvRef.current = chunkErrorsCsv;
            } else {
              // Remove header line from subsequent chunks before appending
              const chunkWithoutHeader = chunkLines.slice(1).join("\n");
              errorsCsvRef.current += "\n" + chunkWithoutHeader;
            }
          }

          sendNextChunk(index);
        }

        if (type === "IMPORT_CSV_ERROR") {
          setIsLoading(false);
          toast.error("Error reading CSV file: " + error);
          workers.forEach((w) => {
            w.terminate();
            w.onmessage = null;
          });
        }
      };

      // Start processing the first chunk for this worker
      sendNextChunk(index);
    });

    // Reset file input to allow importing same file again
    event.target.value = "";
  };

  // Single worker for export
  const workerExportRef = useRef<Worker | null>(null);
  useEffect(() => {
    workerExportRef.current = new Worker(
      new URL("../../../workers/csvWorker.worker.ts", import.meta.url),
      { type: "module" }
    );

    const worker = workerExportRef.current;

    worker.onmessage = (event) => {
      const { type, csv, error, cardsExportedNunber } = event.data;

      if (type === "EXPORT_CSV_SUCCESS") {
        setIsLoading(false);

        const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
        const url = URL.createObjectURL(blob);

        const link = document.createElement("a");
        link.href = url;
        link.download = "cards_board.csv";
        link.click();

        URL.revokeObjectURL(url);
        toast.success(`Exported ${cardsExportedNunber} cards`);
      }

      if (type === "EXPORT_CSV_ERROR") {
        setIsLoading(false);
        toast.error("Error generating CSV: " + error);
      }
    };

    return () => {
      worker.terminate();
    };
  }, [cardsBoard.length]);

  const downloadCSV = ({ onlySelected }: { onlySelected?: boolean }) => {
    if (!cardsBoard.length) {
      toast.error("No cards to export");
      return;
    }

    const minimalCardIndexData: MinimalCardToSendToUploadCsvWorker =
      Object.fromEntries(
        Object.entries(cards)
          .map(([id, card]) => [
            String(card.card_market_ids?.[0]), // key by first card_market_id (string)
            {
              id,
              card_market_id: card.card_market_ids?.[0],
              name: card.name,
            },
          ])
          .filter(([key]) => !!key) // filter out cards without card_market_id
      );

    let cardsBoardToSend = [...cardsBoard];
    if (onlySelected) {
      cardsBoardToSend = [...cardsBoard].filter((card) =>
        cardsClicked.includes(card.uid)
      );
    }

    setIsLoading(true);
    workerExportRef.current?.postMessage({
      type: "EXPORT_CSV",
      payload: {
        cardsBoard: cardsBoardToSend,
        cardIndexData: minimalCardIndexData,
      },
    });

    setModalExport(false);
  };

  if (isLoading) {
    return (
      <LoadingOverlay>
        {importProgress.total && (
          <Box css={{ marginTop: "20px", color: "white", textAlign: "center" }}>
            Importing cards {importProgress.imported} / {importProgress.total}
          </Box>
        )}
      </LoadingOverlay>
    );
  }

  return (
    <FlexBox css={{ gap: 20, alignItems: "center" }}>
      {modalExport && (
        <Modal
          onClose={() => setModalExport(false)}
          modalStyle={{ width: 600 }}
        >
          <FlexBox css={{ gap: 20, justifyContent: "center" }}>
            <PrimaryButton
              disabled={!cardsClicked.length}
              isDisabled={!cardsClicked.length}
              onClick={() => downloadCSV({ onlySelected: true })}
            >
              EXPORT ONLY SELECTED
            </PrimaryButton>
            <PrimaryButton onClick={() => downloadCSV({ onlySelected: false })}>
              EXPORT ALL
            </PrimaryButton>
          </FlexBox>
          <Box css={{ margin: "20px auto 0 auto" }}>
            <SecondaryButton onClick={() => setModalExport(false)}>
              CLOSE
            </SecondaryButton>
          </Box>
        </Modal>
      )}
      <WrapperIcon>
        <Box
          style={{ cursor: cardsBoard?.length ? "pointer" : "not-allowed" }}
          onClick={() => {
            if (cardsBoard?.length) setModalExport(true);
          }}
        >
          <FiUpload size={20} color={cardsBoard?.length ? "green" : "grey"} />
        </Box>
        <IconNameText>Export csv</IconNameText>
      </WrapperIcon>
      <Box>
        <input
          type="file"
          accept=".csv,text/csv"
          onChange={handleImportCSV}
          style={{ display: "none" }}
          id="import-csv"
        />
        <label htmlFor="import-csv">
          <WrapperIcon>
            <Box>
              <FiDownload size={20} color={"green"} />
            </Box>
            <IconNameText>Import csv</IconNameText>
          </WrapperIcon>
        </label>
      </Box>
    </FlexBox>
  );
};
